// Problem Record Workflow Engine — prototype closed-loop stage governance.
//
// Two independent concepts:
//   • Management Status (Draft / Active / On Hold / Escalated / Closed)
//   • Technical Stage   (the 11 closed-loop stages)
//
// All mutations are pure: they take a record and return a new record plus
// audit entries, so persistence can be swapped for a database later.

import {
  STAGE_TEMPLATE,
  type ManagementStatus,
  type ProblemRecord,
  type ProblemStage,
} from "./problem-record-data";

export const PROTOTYPE_NOW = "2026-08-28";

export type EvidenceFieldType = "text" | "textarea" | "yesno" | "select";

export type EvidenceField = {
  key: string;
  label: string;
  type: EvidenceFieldType;
  options?: string[];
  placeholder?: string;
  /** Not always required — conditional stages resolve requirement in the validator. */
  optional?: boolean;
};

export type StageRequirement = {
  key: string;
  intent: string;
  fields: EvidenceField[];
  approvalRequired: boolean;
  approvalRole?: string;
  /** Stage keys that must be Complete before this stage may complete. */
  prerequisites?: string[];
};

export const STAGE_REQUIREMENTS: Record<string, StageRequirement> = {
  signal: {
    key: "signal",
    intent: "Confirm the warranty signal, its source and the measured exposure.",
    approvalRequired: false,
    fields: [
      {
        key: "source",
        label: "Source identified",
        type: "text",
        placeholder: "Top 5 view / analytic source",
      },
      { key: "evidenceConfirmed", label: "Linked claim evidence present", type: "yesno" },
      {
        key: "exposure",
        label: "Exposure / rate captured",
        type: "text",
        placeholder: "Claims, cost, C/1000",
      },
    ],
  },
  definition: {
    key: "definition",
    intent: "Define the problem, its scope and confirmed severity, with a named owner.",
    approvalRequired: false,
    fields: [
      { key: "problemStatement", label: "Problem statement", type: "textarea" },
      { key: "ownerAssigned", label: "Owner assigned", type: "text" },
      { key: "scope", label: "Scope — affected products / car lines", type: "text" },
      { key: "severityConfirmed", label: "Severity / risk confirmed", type: "yesno" },
    ],
  },
  containment: {
    key: "containment",
    intent: "Protect the customer while root cause work proceeds.",
    approvalRequired: false,
    fields: [
      { key: "action", label: "Containment action", type: "textarea" },
      { key: "owner", label: "Containment owner", type: "text" },
      { key: "due", label: "Due date", type: "text", placeholder: "YYYY-MM-DD" },
      { key: "population", label: "Affected population / scope", type: "text" },
      { key: "evidence", label: "Evidence / completion note", type: "textarea" },
    ],
  },
  "root-cause": {
    key: "root-cause",
    intent: "Establish occurrence, escape and systemic cause with verification evidence.",
    approvalRequired: true,
    approvalRole: "Engineering or Quality",
    fields: [
      { key: "occurrence", label: "Occurrence cause", type: "textarea" },
      { key: "detection", label: "Detection / escape cause", type: "textarea" },
      { key: "systemic", label: "Systemic cause", type: "textarea" },
      { key: "method", label: "Method (5-Why, Ishikawa, DOE, teardown…)", type: "text" },
      { key: "verification", label: "Verification evidence", type: "textarea" },
    ],
  },
  corrective: {
    key: "corrective",
    intent: "Permanent corrective action traceable to the verified root cause.",
    approvalRequired: true,
    approvalRole: "Quality or Engineering",
    prerequisites: ["root-cause"],
    fields: [
      { key: "action", label: "Permanent corrective action", type: "textarea" },
      { key: "owner", label: "Owner", type: "text" },
      { key: "due", label: "Due date", type: "text", placeholder: "YYYY-MM-DD" },
      { key: "rootCauseLink", label: "Linkage to root cause", type: "textarea" },
    ],
  },
  eco: {
    key: "eco",
    intent: "Record the engineering change, or the documented rationale for no change.",
    approvalRequired: false,
    fields: [
      { key: "changeRequired", label: "Change required?", type: "yesno" },
      { key: "changeId", label: "ECO / change ID", type: "text", optional: true },
      { key: "affected", label: "Affected part / spec / process", type: "text", optional: true },
      { key: "owner", label: "Change owner", type: "text", optional: true },
      { key: "releaseStatus", label: "Release status / date", type: "text", optional: true },
      {
        key: "rationale",
        label: "Rationale if no change required",
        type: "textarea",
        optional: true,
      },
    ],
  },
  pfmea: {
    key: "pfmea",
    intent: "Feed the learning back into PFMEA and the Control Plan.",
    approvalRequired: false,
    fields: [
      { key: "pfmeaRequired", label: "PFMEA update required?", type: "yesno" },
      { key: "controlPlanRequired", label: "Control Plan update required?", type: "yesno" },
      { key: "linkedIds", label: "Linked row / change IDs", type: "text", optional: true },
      { key: "risk", label: "Risk before / after (or rationale)", type: "text", optional: true },
    ],
  },
  validation: {
    key: "validation",
    intent: "Prove the corrective action works before relying on it.",
    approvalRequired: true,
    approvalRole: "Responsible engineering / quality",
    fields: [
      { key: "plan", label: "Validation plan / test", type: "textarea" },
      { key: "acceptance", label: "Acceptance criteria", type: "text" },
      { key: "sample", label: "Sample / method", type: "text" },
      { key: "result", label: "Result", type: "textarea" },
      {
        key: "verdict",
        label: "Pass / Fail",
        type: "select",
        options: ["", "Pass", "Fail", "Deviation approved"],
      },
    ],
  },
  effectiveness: {
    key: "effectiveness",
    intent: "Confirm the field data actually moved, over a defined monitoring window.",
    approvalRequired: false,
    fields: [
      { key: "baseline", label: "Baseline metric", type: "text" },
      { key: "post", label: "Post-change metric", type: "text" },
      { key: "target", label: "Target", type: "text" },
      { key: "window", label: "Monitoring window", type: "text" },
      { key: "conclusion", label: "Effectiveness conclusion", type: "textarea" },
    ],
  },
  "read-across": {
    key: "read-across",
    intent: "Apply the lesson everywhere the same failure mechanism can exist.",
    approvalRequired: false,
    fields: [
      { key: "lesson", label: "Lesson learned", type: "textarea" },
      { key: "applicability", label: "Applicability / read-across targets", type: "textarea" },
      {
        key: "actions",
        label: "Actions & owners for applicable targets",
        type: "textarea",
        optional: true,
      },
    ],
  },
  prevention: {
    key: "prevention",
    intent: "Lock in prevention and close the record under management approval.",
    approvalRequired: true,
    approvalRole: "Management (sponsor)",
    prerequisites: [
      "signal",
      "definition",
      "containment",
      "root-cause",
      "corrective",
      "eco",
      "pfmea",
      "validation",
      "effectiveness",
      "read-across",
    ],
    fields: [
      { key: "prevention", label: "Prevention actions confirmed", type: "textarea" },
      { key: "closure", label: "Final closure summary", type: "textarea" },
    ],
  },
};

export const STAGE_LABELS: Record<string, string> = Object.fromEntries(
  STAGE_TEMPLATE.map((s) => [s.key, s.label]),
);

const yes = (v?: string) => (v ?? "").trim().toLowerCase() === "yes";
const has = (v?: string) => (v ?? "").trim().length > 0;

/** Returns the list of blocking reasons preventing completion. Empty = may complete. */
export function validateStageCompletion(record: ProblemRecord, stageKey: string): string[] {
  const req = STAGE_REQUIREMENTS[stageKey];
  const stage = record.stages.find((s) => s.key === stageKey);
  if (!req || !stage) return ["Unknown stage."];
  const ev = stage.evidence ?? {};
  const missing: string[] = [];

  for (const pk of req.prerequisites ?? []) {
    const prior = record.stages.find((s) => s.key === pk);
    if (prior && prior.state !== "Complete") {
      missing.push(`Prerequisite stage "${prior.label}" is not complete.`);
    }
  }

  const need = (key: string) => {
    const f = req.fields.find((x) => x.key === key);
    if (f && !has(ev[key])) missing.push(`${f.label} is required.`);
  };

  switch (stageKey) {
    case "signal": {
      const linked =
        (record.linkedClaimIds?.length ?? 0) > 0 || (record.linkedClaims?.length ?? 0) > 0;
      if (!linked) missing.push("No linked claim evidence is attached to this record.");
      need("source");
      need("exposure");
      break;
    }
    case "definition":
      need("problemStatement");
      need("ownerAssigned");
      need("scope");
      break;
    case "containment":
      need("action");
      need("owner");
      need("evidence");
      break;
    case "root-cause":
      need("occurrence");
      need("detection");
      need("systemic");
      need("verification");
      break;
    case "corrective":
      need("action");
      need("owner");
      need("rootCauseLink");
      break;
    case "eco":
      if (!has(ev.changeRequired)) missing.push("Change required? must be answered Yes or No.");
      else if (yes(ev.changeRequired)) {
        need("changeId");
        need("affected");
        need("owner");
        need("releaseStatus");
      } else need("rationale");
      break;
    case "pfmea":
      if (!has(ev.pfmeaRequired) || !has(ev.controlPlanRequired))
        missing.push("Both PFMEA and Control Plan update decisions must be answered.");
      else if ((yes(ev.pfmeaRequired) || yes(ev.controlPlanRequired)) && !has(ev.linkedIds))
        missing.push("Linked row / change IDs are required when an update is needed.");
      else if (!yes(ev.pfmeaRequired) && !yes(ev.controlPlanRequired) && !has(ev.risk))
        missing.push("Documented rationale is required when no update is needed.");
      break;
    case "validation":
      need("plan");
      need("acceptance");
      need("result");
      if (!has(ev.verdict))
        missing.push("A validation verdict (Pass / Fail / Deviation approved) is required.");
      else if (ev.verdict === "Fail")
        missing.push("Validation failed — record a passing result or an approved deviation.");
      break;
    case "effectiveness":
      need("baseline");
      need("post");
      need("window");
      need("conclusion");
      break;
    case "read-across":
      need("lesson");
      need("applicability");
      break;
    case "prevention":
      need("prevention");
      need("closure");
      break;
  }

  if (req.approvalRequired && stage.approvalStatus !== "Approved") {
    missing.push(`${req.approvalRole ?? "Management"} approval is required before completion.`);
  }

  return missing;
}

export function missingEvidenceOnActiveStage(record: ProblemRecord): boolean {
  const active = record.stages.find((s) => s.state === "Active");
  if (!active) return false;
  return validateStageCompletion(record, active.key).length > 0;
}

export function currentStage(record: ProblemRecord): ProblemStage | undefined {
  return (
    record.stages.find((s) => s.state === "Active") ??
    [...record.stages].reverse().find((s) => s.state === "Complete")
  );
}

export function currentStageLabel(record: ProblemRecord): string {
  if (record.managementStatus === "Closed") return "Prevention & Closure";
  return currentStage(record)?.label ?? "Warranty Signal";
}

function audit(
  record: ProblemRecord,
  actor: string,
  action: string,
  clause: string,
): ProblemRecord["activity"] {
  return [{ ts: `${PROTOTYPE_NOW} 00:00`, actor, action, clause }, ...record.activity];
}

function withStage(
  record: ProblemRecord,
  key: string,
  patch: Partial<ProblemStage>,
): ProblemStage[] {
  return record.stages.map((s) => (s.key === key ? { ...s, ...patch } : s));
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function saveStageDraft(
  record: ProblemRecord,
  key: string,
  patch: {
    owner?: string;
    due?: string;
    evidence?: Record<string, string>;
    completionNote?: string;
    approver?: string;
  },
): ProblemRecord {
  const stage = record.stages.find((s) => s.key === key);
  if (!stage) return record;
  return {
    ...record,
    stages: withStage(record, key, {
      owner: patch.owner ?? stage.owner,
      due: patch.due ?? stage.due,
      evidence: { ...(stage.evidence ?? {}), ...(patch.evidence ?? {}) },
      completionNote: patch.completionNote ?? stage.completionNote,
      approver: patch.approver ?? stage.approver,
    }),
  };
}

export function activateStage(record: ProblemRecord, key: string, actor: string): ProblemRecord {
  const stage = record.stages.find((s) => s.key === key);
  if (!stage || stage.state === "Complete") return record;
  const stages = record.stages.map((s) =>
    s.key === key
      ? { ...s, state: "Active" as const }
      : s.state === "Active"
        ? { ...s, state: "Pending" as const }
        : s,
  );
  return {
    ...record,
    stages,
    managementStatus: record.managementStatus === "Draft" ? "Active" : record.managementStatus,
    isDraft: false,
    status: `Active — ${stage.label}`,
    activity: audit(record, actor, `Stage activated — ${stage.label}`, "IATF 16949 §10.2.3"),
  };
}

export function approveStage(record: ProblemRecord, key: string, approver: string): ProblemRecord {
  const stage = record.stages.find((s) => s.key === key);
  if (!stage) return record;
  return {
    ...record,
    stages: withStage(record, key, { approvalStatus: "Approved", approver }),
    activity: audit(record, approver, `Stage approved — ${stage.label}`, "ISO 9001:2015 §9.3"),
  };
}

export type CompleteResult =
  { ok: false; errors: string[] } | { ok: true; record: ProblemRecord; nextStageKey?: string };

export function completeStage(record: ProblemRecord, key: string, actor: string): CompleteResult {
  const errors = validateStageCompletion(record, key);
  if (errors.length) return { ok: false, errors };
  const stage = record.stages.find((s) => s.key === key)!;

  let next: ProblemRecord = {
    ...record,
    stages: withStage(record, key, {
      state: "Complete",
      completedOn: PROTOTYPE_NOW,
      summary: stage.completionNote?.trim() || stage.summary,
    }),
    activity: audit(record, actor, `Stage completed — ${stage.label}`, "IATF 16949 §10.2.3"),
  };

  if (key === "prevention") {
    next = {
      ...next,
      managementStatus: "Closed",
      status: "Closed — Prevention verified",
      activity: audit(
        next,
        actor,
        "Management Status changed to Closed — closure approved",
        "ISO 9001:2015 §10.2.2",
      ),
    };
    return { ok: true, record: next };
  }

  const idx = next.stages.findIndex((s) => s.key === key);
  const nextStage = next.stages.slice(idx + 1).find((s) => s.state !== "Complete");
  next = { ...next, status: nextStage ? `Active — ${nextStage.label}` : next.status };
  return { ok: true, record: next, nextStageKey: nextStage?.key };
}

export function setManagementStatus(
  record: ProblemRecord,
  status: ManagementStatus,
  actor: string,
  reason?: string,
): ProblemRecord {
  const stageLabel = currentStageLabel(record);
  return {
    ...record,
    managementStatus: status,
    holdReason: status === "On Hold" ? (reason ?? record.holdReason) : undefined,
    isDraft: status === "Draft",
    // Technical stage is deliberately untouched.
    status:
      status === "Closed"
        ? "Closed — Prevention verified"
        : status === "On Hold"
          ? `On Hold — ${stageLabel}`
          : status === "Escalated"
            ? `Escalated — ${stageLabel}`
            : status === "Draft"
              ? `Draft — ${stageLabel}`
              : `Active — ${stageLabel}`,
    activity: audit(
      record,
      actor,
      `Management Status changed to ${status}${reason ? ` — ${reason}` : ""} (technical stage unchanged: ${stageLabel})`,
      "ISO 9001:2015 §9.3",
    ),
  };
}
